import * as React from "react";

import {Checkbox, Flag, Label, Pagination, Segment, Table} from "semantic-ui-react";
import {FlagDto, OptionDto, OptionFlagMultiUpdateParamActionType, OptionTextDto} from "../../../services/Client";
import DataAdapterOptionsView from "../DataAdapterOptionsView";
import {SelectType} from "../workflowStatusMultiUpdate/WSMUSummaryUtils";
import PromtFormatter from "src/components/Table/components/reactQuill/PromtFormatter";

export interface FMUSummaryTableProps {
    totalItems: DataAdapterOptionsView[];
    tablePage: number;
    t: reactI18Next.TranslationFunction;
    onChangePage: (pageNumber: number) => void;
    updatedFlags: FlagDto[];
    alreadyExistFlags: FlagDto[];
    itemsToUpdate?: Map<number, OptionDto>;
    handleSelectChange?: (actionType: SelectType, optionDto?: OptionDto, isChecked?: boolean) => void;
    actionType: OptionFlagMultiUpdateParamActionType;
}

const getTableHeaderCells = (t: reactI18Next.TranslationFunction) => {
    return (
        <>
            <Table.HeaderCell>{t("PSL_WF_S_MULTI:OPTION_IDENTIFIER")}</Table.HeaderCell>
            <Table.HeaderCell>{t("PSL_WF_S_MULTI:SHORTTEXT")}</Table.HeaderCell>
            <Table.HeaderCell>{t("PSL_WF_S_MULTI:FLAG")}</Table.HeaderCell>
        </>
    );
};

const getIdentifierCellData = (optionCode: string, optionType?: string) => {
    return (
        <Label size={'large'} className={'whitespace--nowrap'}>
            {optionCode}
            <Label.Detail>
                {optionType}
            </Label.Detail>
        </Label>
    );
};

const getTextsCellData = (texts: OptionTextDto[]) => {
    texts = texts.filter((p) => {
        if (p.language) {
            return p.language.code === 'cc' || p.language.code === 'de';
        }
        return true;
    });
    return (
        <Segment.Group horizontal={true}>
            {texts.sort((a, b) =>
                (a.language && b.language && a.language.code && b.language.code && a.language.code > b.language.code) ? 1 : -1)
                .map((txt: OptionTextDto, idx: number) =>
                    <Segment
                        key={"txt-" + idx}
                        className={'texts-segment'}
                    >
                        <Flag
                            name={txt.language && txt.language.code === "de" ? "de" : "gb"}
                            className={'texts-flag'}

                        />
                        {txt.shorttext || ""}
                    </Segment>
                )}
        </Segment.Group>
    );
};
const getRenderFlags = (parentArray: FlagDto[], compareArray: FlagDto[]) => {
    const renderUpdatedFlags: FlagDto[] = [];
    const renderDifferenceFlags: FlagDto[] = [];
    parentArray.map(flag => {
        const found = compareArray.find(p => p.objectId === flag.objectId);
        if (found) {
            renderUpdatedFlags.push(flag);
        } else {
            renderDifferenceFlags.push(flag);
        }
    });
    return {
        renderUpdatedFlags,
        renderDifferenceFlags
    };
};

const getUpdatedFlags = (actionType: OptionFlagMultiUpdateParamActionType, updatedFlags: FlagDto[], alreadyExistFlags: FlagDto[]) => {
    let renderUpdatedFlags;
    let renderDifferenceFlags;
    let getRender;
    if (actionType === OptionFlagMultiUpdateParamActionType.ADD) {
        getRender = getRenderFlags(updatedFlags, alreadyExistFlags);
    } else {
        getRender = getRenderFlags(alreadyExistFlags, updatedFlags);
    }
    renderUpdatedFlags = getRender.renderUpdatedFlags;
    renderDifferenceFlags = getRender.renderDifferenceFlags;
    const color = actionType === OptionFlagMultiUpdateParamActionType.ADD ? 'green' : 'red';
    return (
        <>
            {renderDifferenceFlags.map(flag =>
                <Label key={flag.objectId} color={color} tag={true} style={{ marginBottom: 2 }}>
                    {flag.textLabel}
                </Label>
            )
            }
            <br />
            {renderUpdatedFlags.map(flag =>
                <Label key={flag.objectId} color='grey' tag={true} style={{ marginBottom: 2 }}>
                    {flag.textLabel}
                </Label>
            )
            }
        </>
    );
};

const getTablePagination = (invalidTablePage: number, totalItemsCount: number, onChangePage: (pageNumber: number) => void) => {
    return (
        <Pagination
            activePage={invalidTablePage}
            totalPages={Math.ceil(totalItemsCount / 10)}
            onPageChange={(e, c) => onChangePage(c.activePage as number)}
        />
    );
};

export const FMUSummaryTable: React.StatelessComponent<FMUSummaryTableProps> = (props: FMUSummaryTableProps) => {
    
    const allSelected = props.totalItems.find(p => p.dto.isChecked === false) === undefined;
    const items = props.totalItems.slice((props.tablePage - 1) * 10, props.tablePage * 10);

    return (
        <Table
            id={'psl-wsmu-summary-table'}
            celled={true}
            structured={true}
            textAlign={'center'}
        >
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        <Checkbox
                            slider={true}
                            checked={allSelected}
                            onChange={() => props.handleSelectChange && props.handleSelectChange(allSelected ? SelectType.DeselectAll : SelectType.SelectAll)}
                        />
                    </Table.HeaderCell>
                    {getTableHeaderCells(props.t)}
                </Table.Row>
            </Table.Header>

            <Table.Body>
                {items.map((item: DataAdapterOptionsView, index: number) => {
                    return (
                        <Table.Row key={'row-' + index}>
                            <Table.Cell>
                                <Checkbox
                                    slider={true}
                                    checked={item.dto.isChecked}
                                    onChange={(e: any, data: any) => props.handleSelectChange && props.handleSelectChange(SelectType.SingleSelect, item.dto, data.checked)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                {getIdentifierCellData(item.optionBasicView, item.optionTypeView)}
                            </Table.Cell>
                            <Table.Cell>
                                {getTextsCellData(item.texts)}
                            </Table.Cell>
                            <Table.Cell>
                                {getUpdatedFlags(props.actionType, props.updatedFlags, props.alreadyExistFlags)}
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table.Body>

            <Table.Footer>
                <Table.Row>
                    <Table.HeaderCell colSpan={4}>
                        {getTablePagination(props.tablePage, props.totalItems.length, props.onChangePage)}
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Footer>
        </Table>
    );
};



// WEBPACK FOOTER //
// ./src/views/OptionsView/MultipleFlagUpdate/FMUSummaryTable.tsx